import {
  setCollectionProducts,
  setLastCollection,
} from '@/store/actions/collectionInfo';
import { generateCards } from '@/utils/Catalog';
import { IPagination } from '@/interfaces/index';
import { ICollection } from './ICollectionCmp';
import { CardGridPagination } from '@/components/organisms';
import { Typography } from '@/components/atoms';
import { useTranslation } from 'next-i18next';
import { moveToCollectionNewPagination } from '@/utils/Collections';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { RootState } from '@/store/store';
import { setLoading } from '@/store/actions/loadingOverlay';
import { Product, ProductSearch } from '@/lib/models/types/product';
import { useSWRResult } from '@/hooks/useSwrResult';
import { mapSearchResult } from '@/lib/models/mappers/product';
import { httpGateway } from '@/shared/HttpGateway';
import { mapResult } from '@/utils/typings/result';

const CollectionListing = styled.div`
  position: relative;
  width: 100%;
  /* overflow-x: hidden; */
`;

const ProductsNumberTypography = styled(Typography)`
  color: ${props => props.theme.paletteDefault.grey1};
`;

const ProductsNumberWrapper = styled.div`
  padding: 0 8px;
`;

const Collection: React.FC<ICollection> = ({
  productsQuery,
  scrollToRef,
  showItemsNumber = true,
  serverQueryResult,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const router = useRouter();

  //state
  const [currentQuery, setCurrentQuery] = useState<string | null>(null);

  //from redux
  const lastCollection = useSelector(
    (state: RootState) => state.collectionInfoReducer.lastCollection
  );
  const loadingProducts = useSelector(
    (state: RootState) => state.loadingOverlayReducer.products
  );

  let products: ProductSearch | null = null;

  if (!productsQuery && serverQueryResult?.items) {
    products = serverQueryResult;
  } else {
    if (productsQuery !== currentQuery) {
      setCurrentQuery(productsQuery);
    }
  }

  const { data, error } = useSWRResult(currentQuery, (url: string) =>
    httpGateway.get(url).then(res => mapResult(res, mapSearchResult))
  );

  useEffect(() => {
    if (serverQueryResult?.pagination?.totalItems !== undefined) {
      dispatch(
        setCollectionProducts({
          numProducts: serverQueryResult.pagination.totalItems,
        })
      );
    }
  }, []);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (url.startsWith('/products') || !lastCollection) {
        var scrollY = window.pageYOffset;
        dispatch(
          setLastCollection({
            url: router.asPath,
            scrollY,
          })
        );
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.asPath]);

  let cards = null;

  if (productsQuery && data?.items) {
    const newProducts = data.items;

    let newProductsArray: Product[] = [];

    if (newProducts && newProducts.length > 0) {
      newProductsArray = newProducts?.map((item, index) => {
        (item as any).key = index.toString() + new Date().getTime();

        return item;
      });
    }

    const productItems: ProductSearch = {
      items: newProductsArray,
      facets: [],
      pagination: data.pagination || null,
      metadata: {},
    };

    products = productItems;
  }

  cards = generateCards(products);

  let pagination: IPagination | null = null;
  if (products && products.pagination?.itemsPerPage) {
    pagination = {
      totalPages: Math.ceil(
        (products.pagination?.totalItems || 0) /
          products.pagination?.itemsPerPage
      ),
      pathname: router.asPath.split('?')[0],
      active: products.pagination?.currentPage,
    };
  }

  let totalProductsNumber = null;
  if (
    products?.pagination?.totalItems &&
    products?.pagination?.totalItems > 0
  ) {
    totalProductsNumber = products?.pagination?.totalItems;
  }

  useEffect(() => {
    if (currentQuery) {
      dispatch(setLoading({ products: true }));
      if (lastCollection?.scrollY && router.asPath === lastCollection.url) {
        window.scroll(0, lastCollection.scrollY);
      } else {
        window.scroll(0, scrollToRef?.current?.offsetTop || 0);
      }
    }
  }, [currentQuery]);

  useEffect(() => {
    if (data || error) {
      if (data) {
        const totalProductsNumber = data?.pagination?.totalItems;

        if (totalProductsNumber !== undefined) {
          dispatch(setCollectionProducts({ numProducts: totalProductsNumber }));
        }
      }
      dispatch(setLoading({ products: false }));
    }
  }, [data, error]);

  return (
    <CollectionListing>
      {showItemsNumber && totalProductsNumber && (
        <ProductsNumberWrapper>
          <ProductsNumberTypography datatype="caption">
            {totalProductsNumber}{' '}
            {t('common:product', {
              count: totalProductsNumber,
            })}
          </ProductsNumberTypography>
        </ProductsNumberWrapper>
      )}
      <CardGridPagination
        template="collection"
        itemsArray={cards}
        pagination={pagination}
        onPageChange={(pageNumber: number) => {
          moveToCollectionNewPagination(
            router,
            pageNumber,
            scrollToRef || null
          );
        }}
        isLoading={loadingProducts}
        max={48}
      />
    </CollectionListing>
  );
};

export default Collection;
